import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Notification, NotificationType } from 'edenred-ui';
import { useTheme } from 'styled-components';

import { SectionWrapper } from '@epi-shared/components/BenefitGroups/SettingsSections/SectionWrapper/SectionWrapper';
import { BenefitType } from '@epi-constants/benefitTypes';
import {
  IBenefitDefaultSettings,
  ICreateBenefitGroupForm
} from '@epi-models/pages/BenefitGroups';

import { GeneralSettings } from './GeneralSettings/GeneralSettings';
import { BenefitGroupSettings } from './BenefitGroupSettings/BenefitGroupSettings';
import { GeneralSettingsSwitch } from './GeneralSettings/GeneralSettingsSwitch';
import { useTranslation } from 'react-i18next';
import { BenefitInformationalModal } from '@epi-shared/components/BenefitInformationalModal/BenefitInformationalModal';

interface LunchSettingsProps {
  defaultSettings: IBenefitDefaultSettings;
}

const LunchSettings = ({ defaultSettings }: LunchSettingsProps) => {
  const { watch, setValue, formState } =
    useFormContext<ICreateBenefitGroupForm>();
  const { primaryBackgroundAltColor, lightBlue5 } = useTheme();
  const [isToggleActive, setIsToggleActive] = useState(false);
  const { t } = useTranslation();

  const value = watch(`benefitSettings.lunch`);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const maxLunchGlobal =
    defaultSettings.lunchGlobalSettings?.maxAmountPerPeriod;
  const maxValue = defaultSettings.contractedBenefits.find(
    ({ benefitType }) => benefitType === BenefitType.Lunch
  )?.maxAmount;
  const canApplyFundsLoadingChangeImmediately =
    defaultSettings.lunchGlobalSettings
      ?.canApplyFundsLoadingChangeImmediately ?? false;
  const fundsLoadingTypeGlobal =
    defaultSettings.lunchGlobalSettings?.fundsLoadingType;
  const removeToggle = !defaultSettings.lunchGlobalSettings;
  const hasScheduledLoads =
    defaultSettings.lunchGlobalSettings?.isPendingScheduledLoadsForBenefit ??
    false;
  const generalSettingsActive = removeToggle || isToggleActive;

  const handleToggleGeneralSettings = () => {
    const lunchDefaults = formState.defaultValues?.benefitSettings?.lunch;
    if (generalSettingsActive && lunchDefaults) {
      const { isMaximumSpendingLimit, maxAmountPerPeriod, fundsLoadingType } =
        lunchDefaults;

      setValue(
        'benefitSettings.lunch.isMaximumSpendingLimit',
        isMaximumSpendingLimit
      );
      setValue('benefitSettings.lunch.maxAmountPerPeriod', maxAmountPerPeriod, {
        shouldValidate: true
      });
      setValue('benefitSettings.lunch.fundsLoadingType', fundsLoadingType);
    }

    setIsToggleActive(prev => !prev);
  };

  if (!value) {
    return null;
  }

  const backgroundColor = generalSettingsActive
    ? lightBlue5
    : primaryBackgroundAltColor;

  return (
    <SectionWrapper benefitName={BenefitType.Lunch}>
      <Box
        p={3}
        bgcolor={backgroundColor}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        {!removeToggle && (
          <GeneralSettingsSwitch
            checked={generalSettingsActive}
            toggleChecked={handleToggleGeneralSettings}
          />
        )}
        {isToggleActive && (
          <Box>
            <Notification
              text={t(
                'containers.benefit_groups.general_settings_notification'
              )}
              type={NotificationType.Info}
              id="lunch-generals-settings-active-notification"
            />
          </Box>
        )}
        <GeneralSettings
          maxValue={maxValue}
          disabled={!generalSettingsActive}
          maxLunchGlobal={maxLunchGlobal}
          fundsLoadingType={fundsLoadingTypeGlobal}
          canApplyFundsLoadingChangeImmediately={
            canApplyFundsLoadingChangeImmediately
          }
          hasScheduledLoads={hasScheduledLoads}
          handleCloseModal={setShowWarningModal}
        />
      </Box>
      <BenefitGroupSettings />
      <BenefitInformationalModal
        showInformationModal={showWarningModal}
        handleCloseModal={() => setShowWarningModal(false)}
      />
    </SectionWrapper>
  );
};

export { LunchSettings };
